import { readVisitorId } from '../visitorId';
import { AnalyticsProvider } from './AnalyticsProvider';
import { hasStatisticsConsent } from './GoogleAnalyticsProvider';
import { sanitizeEventName, stripSensitive } from './safeTrackingProperties';

type FullStoryWindow = Window & {
  FS?: (command: string, payload?: Record<string, unknown>) => void;
};

/**
 * Same FullStory org as www.audicus.com. The cookie is shared across
 * audicus.com subdomains, so a visit can continue from the main site.
 * This does not call setIdentity.
 */
export class FullStoryProvider implements AnalyticsProvider {
  name = 'fullstory';

  private pending: Array<{
    event: string;
    properties?: Record<string, unknown>;
  }> = [];

  private started = false;

  initialize(): void {
    const orgId = process.env.REACT_APP_FULLSTORY_ORG_ID;
    if (!orgId) {
      return;
    }

    const start = () => {
      if (this.started || (window as FullStoryWindow).FS) {
        this.tagVisitor();
        return;
      }
      this.started = true;
      const script = document.createElement('script');
      script.text = fullStorySnippet(orgId);
      document.head.appendChild(script);
      this.tagVisitor();
      this.flush();
    };

    if (!process.env.REACT_APP_COOKIEBOT_ID) {
      start();
      return;
    }

    const onConsent = () => {
      if (hasStatisticsConsent() === true) {
        start();
      }
    };
    window.addEventListener('CookiebotOnAccept', onConsent);
    window.addEventListener('CookiebotOnConsentReady', onConsent);
    window.addEventListener('CookiebotOnLoad', onConsent);
    onConsent();
  }

  track(event: string, properties?: Record<string, any>): void {
    const safe = stripSensitive(properties);
    const fs = (window as FullStoryWindow).FS;
    if (typeof fs === 'function') {
      fs('trackEvent', {
        name: sanitizeEventName(event),
        properties: safe,
      });
      return;
    }
    this.pending.push({ event: sanitizeEventName(event), properties: safe });
  }

  identify(): void {
    // Names and emails stay out of FullStory.
  }

  page(name?: string): void {
    if (name) {
      this.track(name);
    }
  }

  private tagVisitor(): void {
    const visitorId = readVisitorId(document.cookie);
    const fs = (window as FullStoryWindow).FS;

    if (!visitorId || typeof fs !== 'function') {
      return;
    }

    fs('setProperties', {
      type: 'user',
      properties: { visitor_id: visitorId },
    });
  }

  private flush(): void {
    const fs = (window as FullStoryWindow).FS;
    if (typeof fs !== 'function') {
      return;
    }
    const batch = this.pending.splice(0, this.pending.length);
    batch.forEach(({ event, properties }) => {
      fs('trackEvent', { name: event, properties });
    });
  }
}

function fullStorySnippet(orgId: string): string {
  return `!function(m,n,e,t,l,o,g,y){var u,a,f=function(h){
return!(h in m)||(m.console&&m.console.log&&m.console.log("FullStory namespace conflict. Please use a different namespace."),!1)}(l)
;f&&(g=m[l]=function(){var b=function(b,d,j,r){r=r||2;var i,c=/Async$/;return c.test(b)&&(b=b.replace(c,""),
"function"==typeof Promise)?new Promise((function(i,c){h(b,d,j,i,c,r)})):h(b,d,j,i,i,r)};function h(h,d,j,r,i,c){
return b._api?b._api(h,d,j,r,i,c):(b.q&&b.q.push([h,d,j,r,i,c]),null)}return b.q=[],b}(),y=function(b){function h(h){
"function"==typeof h[4]&&h[4](new Error(b))}var d=g.q;if(d){for(var j=0;j<d.length;j++)h(d[j]);d.length=0,d.push=h}},function(){
var b="script",d=n.createElement(b);d.async=!0,d.crossOrigin="anonymous",d.src="https://"+t+"?org="+o,d.setAttribute("data-fs-namespace",l),
d.onerror=function(){y("Error loading "+t)};var c=n.getElementsByTagName(b)[0]
;c&&c.parentNode?c.parentNode.insertBefore(d,c):n.head.appendChild(d)}(),function(){function b(){}function h(b,h,d){g(b,h,d,1)}function d(b,d,j){
h("setProperties",{type:b,properties:d},j)}function j(b,h){d("user",b,h)}function r(b,h,d){j({uid:b},d),h&&j(h,d)}g.identify=r,g.setUserVars=j,
g.identifyAccount=b,g.clearUserCookie=b,g.setVars=d,g.event=function(b,d,j){h("trackEvent",{name:b,properties:d},j)},g.anonymize=function(){r(!1)
},g.shutdown=function(){h("shutdown")},g.restart=function(){h("restart")},g.log=function(b,d){h("log",{level:b,msg:d})},g.consent=function(b){
h("setIdentity",{consent:!arguments.length||b})}}(),u="fetch",a="XMLHttpRequest",g._w={},g._w[a]=m[a],g._w[u]=m[u],m[u]&&(m[u]=function(){
return g._w[u].apply(this,arguments)}),g("init",{env:{orgId:o,host:e,script:t}}),g._v="2.1.0")
}(window,document,"fullstory.com","edge.fullstory.com/s/fs.js","FS",${JSON.stringify(orgId)});`;
}
